import { fetchProfileMe, fetchSyncStatus, ProfileMe, SyncStatus } from './profileApi';

/** Цифр в национальном номере Казахстана (без кода страны +7) */
export const PHONE_NATIONAL_DIGITS = 10;

/**
 * Национальная часть номера (до 10 цифр, без +7) из любого написания:
 * "+7 701 123 45 67", "+77011234567", "87011234567", "7011234567".
 * Правила совпадают с backend/services/profileView.js (phoneNationalDigits).
 *
 * Число после "+" всегда начинается с кода страны: "+7 701 123 45 6" - это 9 цифр номера,
 * а не 10 цифр без кода (раньше к такому вводу дописывалась лишняя 7 и номер сдвигался).
 */
export const phoneNationalDigits = (raw: string | null | undefined): string => {
  const value = typeof raw === 'string' ? raw.trim() : '';
  if (value.startsWith('+')) {
    const digits = value.slice(1).replace(/\D/g, '');
    return (digits[0] === '7' ? digits.slice(1) : digits).slice(0, PHONE_NATIONAL_DIGITS);
  }
  const digits = value.replace(/\D/g, '');
  if (digits.length === PHONE_NATIONAL_DIGITS) return digits;
  if (digits[0] === '7' || digits[0] === '8') return digits.slice(1, PHONE_NATIONAL_DIGITS + 1);
  return digits.slice(0, PHONE_NATIONAL_DIGITS);
};

/** Маска +7 000 000 00 00 по национальной части номера; пустая национальная часть - префикс "+7 " */
export const formatNationalPhone = (national: string): string => {
  const groups = [national.slice(0, 3), national.slice(3, 6), national.slice(6, 8), national.slice(8, 10)]
    .filter(group => group.length > 0);
  return groups.length > 0 ? `+7 ${groups.join(' ')}` : '+7 ';
};

/**
 * Форматирует телефонный номер по маске: +7 000 000 00 00
 * @param raw Строка с номером телефона (сырые цифры или форматированный текст)
 * @returns Строка в формате "+7 000 000 00 00" или частичная маска в процессе ввода
 */
export const formatPhoneWithSpaces = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const trimmed = raw.trim();
  if (trimmed === '' || trimmed === '+') return '';
  const national = phoneNationalDigits(raw);
  if (!national) return /\d/.test(trimmed) ? '+7 ' : '';
  return formatNationalPhone(national);
};

/**
 * Очищает номер телефона для отправки на бэкенд (БЕЗ разбиения на группы и пробелов)
 * Возвращает формат: +77011234567 или пустую строку
 */
export const cleanPhoneForBackend = (phone: string | null | undefined): string => {
  const national = phoneNationalDigits(phone);
  return national ? `+7${national}` : '';
};

/**
 * Обработчик ввода номера телефона с маской +7 000 000 00 00
 */
export const handlePhoneInputChange = (
  value: string,
  setter: (val: string) => void
) => {
  if (!value || value === '+' || value === '+7') {
    setter('');
    return;
  }
  if (value === '+7 ') {
    setter('+7 ');
    return;
  }
  setter(formatPhoneWithSpaces(value));
};

/**
 * При фокусе на пустое поле автоматически подставляет префикс +7
 */
export const handlePhoneFocus = (
  value: string,
  setter: (val: string) => void
) => {
  if (!value || value.trim() === '') {
    setter('+7 ');
  }
};

/**
 * При потере фокуса (blur), если не введено ни одной цифры кроме +7, очищает поле
 */
export const handlePhoneBlur = (
  value: string,
  setter: (val: string) => void
) => {
  if (!value || value.trim() === '+7' || value.trim() === '+' || value.trim() === '') {
    setter('');
  }
};

/** Синхронизатор касс остановлен в рабочее время аптеки; не удалось проверить (sync = null) - не блокирует */
export const isSyncBlocking = (sync: SyncStatus | null, workingHours: string): boolean =>
  Boolean(sync && !sync.isRunning && isCurrentTimeWithinWorkingHours(workingHours));

/**
 * Флаги обязательных требований профиля из ответа GET /api/v2/profile/me (заполненность считает сервер):
 * персональные данные и корпоративная почта (все интерфейсы), синхронизатор касс (кабинет аптеки).
 * Сохраняет флаги в localStorage и рассылает событие 'profileStatusChanged'.
 */
export const applyProfileStatus = (me: ProfileMe, syncBlocked: boolean): boolean => {
  const flags: Record<string, boolean> = {
    isPersonalIncomplete: !me.completeness.personalComplete,
    isMailIncomplete: me.completeness.items.some(item => item.key === 'mail' && !item.done),
    isSyncIncomplete: syncBlocked
  };
  const isOverallIncomplete = Object.keys(flags).some(key => flags[key]);

  localStorage.setItem('profileIncomplete', isOverallIncomplete ? 'true' : 'false');
  Object.keys(flags).forEach(key => localStorage.setItem(key, flags[key] ? 'true' : 'false'));
  window.dispatchEvent(new Event('profileStatusChanged'));
  return isOverallIncomplete;
};

/**
 * Проверка обязательных требований профиля текущей сессии (пользователь определяется сервером по сессии).
 * Синхронизатор касс проверяется только для интерфейса dashboard. При ошибке запроса флаги не меняются
 * и возвращается прежнее значение profileIncomplete.
 */
export const verifyAndSyncProfileStatus = async (username: string): Promise<boolean> => {
  if (!username) return false;
  try {
    const me = await fetchProfileMe();
    let sync: SyncStatus | null = null;
    if (me.role.targetInterface === 'dashboard') {
      try {
        sync = await fetchSyncStatus(me.profile.kkm, me.profile.username);
      } catch (syncErr) {
        console.warn('Проверка статуса синхронизатора:', syncErr);
      }
    }
    return applyProfileStatus(me, isSyncBlocking(sync, me.profile.workingHours));
  } catch (err) {
    console.error('Ошибка проверки статуса профиля:', err);
  }
  return localStorage.getItem('profileIncomplete') === 'true';
};

/**
 * Проверяет, находится ли текущее время в интервале рабочего времени аптеки.
 * Форматы workingHours:
 * - "Круглосуточно" / "24/7" / "24 часа" -> true
 * - "08:30 - 00:00" -> от 08:30 до полуночи (24:00)
 * - "08:00 - 22:00" -> от 08:00 до 22:00
 * - "08:00 - 02:00" -> переход через полночь (08:00..23:59 и 00:00..02:00)
 */
export const isCurrentTimeWithinWorkingHours = (
  workingHours?: string | null,
  date: Date = new Date()
): boolean => {
  if (!workingHours || typeof workingHours !== 'string') {
    return true; // Если график не задан, по умолчанию считаем рабочим
  }

  const clean = workingHours.trim().toLowerCase();
  if (
    clean.includes('круглосуточно') ||
    clean.includes('24/7') ||
    clean.includes('24 часа') ||
    clean.includes('24/24')
  ) {
    return true;
  }

  const delimiter = clean.includes('-') ? '-' : '-';
  if (!clean.includes(delimiter)) {
    return true;
  }

  const parts = clean.split(delimiter).map(p => p.trim());
  if (parts.length < 2) return true;

  const parseTimeToMinutes = (timeStr: string, isCloseTime: boolean = false): number | null => {
    const match = timeStr.match(/(\d{1,2})[:.](\d{2})/);
    if (!match) return null;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    if (isNaN(hours) || isNaN(minutes)) return null;

    // Если время закрытия указано как "00:00", это означает конец дня (полночь 24:00 = 1440 минут)
    if (isCloseTime && hours === 0 && minutes === 0) {
      return 24 * 60;
    }
    if (hours === 24 && minutes === 0) {
      return 24 * 60;
    }
    return hours * 60 + minutes;
  };

  const openMinutes = parseTimeToMinutes(parts[0], false);
  const closeMinutes = parseTimeToMinutes(parts[1], true);

  if (openMinutes === null || closeMinutes === null) {
    return true;
  }

  const currentMinutes = date.getHours() * 60 + date.getMinutes();

  if (closeMinutes > openMinutes) {
    // Обычная дневная смена, например 08:30 - 22:00 или 08:30 - 00:00 (1440)
    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  } else if (closeMinutes < openMinutes) {
    // Ночная смена с переходом через полночь, например 08:00 - 02:00
    return currentMinutes >= openMinutes || currentMinutes < closeMinutes;
  } else {
    return true;
  }
};

/**
 * Возвращает точное и понятное пользователю сообщение о причине блокировки выхода или перехода.
 */
export const getProfileBlockingMessage = (action: 'navigate' | 'logout' | 'services' = 'navigate'): string => {
  const isSync = localStorage.getItem('isSyncIncomplete') === 'true';
  const isPersonal = localStorage.getItem('isPersonalIncomplete') === 'true';
  const isMail = localStorage.getItem('isMailIncomplete') === 'true';

  // Если заблокировано из-за остановленного синхронизатора касс:
  if (isSync) {
    if (isPersonal) {
      if (action === 'logout') {
        return 'Выход из системы заблокирован: заполните персональные данные профиля и запустите службу «Синхронизатор» на кассе.';
      }
      return 'Переход в другие разделы заблокирован: необходимо заполнить персональные данные профиля и запустить службу «Синхронизатор» на кассе.';
    }
    if (action === 'logout') {
      return 'Выход из системы заблокирован: служба «Синхронизатор» не запущена («Выгрузка прекращена»). Запустите службу на кассе.';
    }
    return 'Переход в другие разделы заблокирован: служба «Синхронизатор» не запущена («Выгрузка прекращена»). Запустите службу синхронизатора на кассе для продолжения работы.';
  }

  // Если не заполнены персональные данные:
  if (isPersonal) {
    if (action === 'logout') {
      return 'Нельзя выйти из системы, пока не будут заполнены обязательные персональные данные профиля (ФИО, телефоны, ИИН, время работы)';
    }
    return 'Для перехода в другие разделы необходимо заполнить обязательные персональные данные профиля (ФИО, телефоны, ИИН, время работы)';
  }

  // Почта обязательна во всех интерфейсах: через нее идут восстановление пароля и подтверждение операций
  if (isMail) {
    if (action === 'logout') {
      return 'Нельзя выйти из системы, пока не подключена корпоративная почта';
    }
    return 'Для перехода в другие разделы необходимо подключить корпоративную почту';
  }

  if (action === 'logout') {
    return 'Нельзя выйти из системы, пока не будут выполнены обязательные требования профиля';
  }
  return 'Для перехода в другие разделы необходимо выполнить обязательные требования профиля';
};

